import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, ShoppingBag, Users, DollarSign, Plus, Edit, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["pending", "awaiting_payment", "paid", "processing", "shipped", "delivered", "cancelled"];

const StatCard = ({ icon: Icon, label, value, tint }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-5">
    <div className={`w-10 h-10 rounded-md flex items-center justify-center ${tint}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="mt-3 text-xs text-slate-500 uppercase font-bold tracking-wider">{label}</div>
    <div className="mt-1 text-2xl font-black text-slate-900">{value}</div>
  </div>
);

const ProductForm = ({ open, onClose, editing, categories, onSaved }) => {
  const empty = {
    sku: "", name: "", slug: "", price: 0, compare_at_price: 0,
    category: "", subcategory: "", brand: "", images: "", stock: 100,
    unit: "each", featured: false, description: "", short_description: "",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        ...editing,
        compare_at_price: editing.compare_at_price || 0,
        images: (editing.images || []).join(", "),
      });
    } else setForm(empty);
  }, [editing, open]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price),
      compare_at_price: Number(form.compare_at_price) || null,
      stock: Number(form.stock),
      images: form.images.split(",").map((s) => s.trim()).filter(Boolean),
      slug: form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, "-") + "-" + form.sku.toLowerCase(),
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Product" : "Add Product"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>SKU</Label>
              <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} required />
            </div>
            <div>
              <Label>Brand</Label>
              <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Price ($)</Label>
              <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
            </div>
            <div>
              <Label>Compare-at Price ($)</Label>
              <Input type="number" step="0.01" value={form.compare_at_price} onChange={(e) => setForm({ ...form, compare_at_price: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, subcategory: "" })}>
                <SelectTrigger><SelectValue placeholder="Choose category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subcategory</Label>
              <Input value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Stock</Label>
              <Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
            </div>
            <div>
              <Label>Unit</Label>
              <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Images (comma-separated URLs)</Label>
            <Textarea rows={2} value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} />
          </div>
          <div>
            <Label>Short Description</Label>
            <Input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
          </div>
          <div className="flex items-center gap-2">
            <input id="feat" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
            <Label htmlFor="feat" className="cursor-pointer">Featured product</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#0b3d75] hover:bg-[#0a4e94]">
              {saving ? "Saving..." : editing ? "Save Changes" : "Create Product"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Admin = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") {
      navigate("/login");
      return;
    }
    refresh();
  }, [user, loading]);

  const refresh = async () => {
    try {
      const [s, p, o, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/products", { params: { limit: 100 } }),
        api.get("/admin/orders"),
        api.get("/categories"),
      ]);
      setStats(s.data);
      setProducts(p.data.items);
      setOrders(o.data);
      setCategories(c.data.categories || []);
    } catch (err) {
      toast.error("Failed to load admin data");
    }
  };

  const delProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Deleted");
      refresh();
    } catch { toast.error("Delete failed"); }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status });
      toast.success("Status updated");
      refresh();
    } catch { toast.error("Update failed"); }
  };

  if (loading || !user || user.role !== "admin") {
    return <div className="p-16 text-center text-slate-500">Loading admin...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Admin Dashboard</h1>
          <p className="text-slate-500 mt-1">Manage products, orders and view analytics.</p>
        </div>
        <Link to="/" className="text-sm text-[#0b3d75] font-semibold hover:text-amber-600">← Back to store</Link>
      </div>

      {stats && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={DollarSign} label="Revenue (paid)" value={formatMoney(stats.total_revenue)} tint="bg-emerald-100 text-emerald-700" />
          <StatCard icon={ShoppingBag} label="Orders" value={stats.total_orders} tint="bg-amber-100 text-amber-700" />
          <StatCard icon={Package} label="Products" value={stats.total_products} tint="bg-blue-100 text-blue-700" />
          <StatCard icon={Users} label="Customers" value={stats.total_users} tint="bg-purple-100 text-purple-700" />
        </div>
      )}

      <Tabs defaultValue="orders" className="mt-8">
        <TabsList>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="products">Products</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="p-3 font-bold">Order</th>
                    <th className="p-3 font-bold">Customer</th>
                    <th className="p-3 font-bold">Items</th>
                    <th className="p-3 font-bold">Total</th>
                    <th className="p-3 font-bold">Date</th>
                    <th className="p-3 font-bold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {orders.map((o) => (
                    <tr key={o.id} className="hover:bg-slate-50">
                      <td className="p-3 font-mono text-xs">{o.order_number}</td>
                      <td className="p-3">
                        <div className="font-semibold">{o.shipping.full_name}</div>
                        <div className="text-xs text-slate-500">{o.shipping.email}</div>
                      </td>
                      <td className="p-3">{o.items.length}</td>
                      <td className="p-3 font-bold text-[#0b3d75]">{formatMoney(o.total)}</td>
                      <td className="p-3 text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                      <td className="p-3">
                        <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                          <SelectTrigger className="w-40 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                  {orders.length === 0 && (
                    <tr><td colSpan="6" className="p-8 text-center text-slate-500">No orders yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="products" className="mt-4">
          <div className="flex justify-between items-center mb-3">
            <div className="text-sm text-slate-600">{products.length} products</div>
            <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-[#0b3d75] hover:bg-[#0a4e94]">
              <Plus className="w-4 h-4 mr-2" /> Add Product
            </Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="p-3 font-bold">Product</th>
                    <th className="p-3 font-bold">SKU</th>
                    <th className="p-3 font-bold">Category</th>
                    <th className="p-3 font-bold">Price</th>
                    <th className="p-3 font-bold">Stock</th>
                    <th className="p-3 font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {products.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <img src={p.images?.[0]} alt="" className="w-10 h-10 object-contain rounded border" />
                          <div>
                            <div className="font-semibold text-slate-900 line-clamp-1">{p.name}</div>
                            {p.featured && <Badge className="bg-amber-100 text-amber-800 text-[10px]">Featured</Badge>}
                          </div>
                        </div>
                      </td>
                      <td className="p-3 font-mono text-xs">{p.sku}</td>
                      <td className="p-3 capitalize text-xs">{p.category.replace(/-/g, " ")}</td>
                      <td className="p-3 font-bold text-[#0b3d75]">{formatMoney(p.price)}</td>
                      <td className="p-3">{p.stock}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => { setEditing(p); setDialogOpen(true); }}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => delProduct(p)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <ProductForm
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editing={editing}
        categories={categories}
        onSaved={refresh}
      />
    </div>
  );
};

export default Admin;
